import { inject, Injectable } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';

/**
 * Set to `true` in a request's HttpContext to suppress the global error toast below
 * (e.g. for type-ahead search, where a failed request should fail silently/inline
 * rather than interrupt the user with a notification).
 */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);
import {
  Observable,
  switchMap,
  throwError,
  ReplaySubject,
  filter,
  take,
  finalize,
} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { IErrorModel, ILoginResponse } from '@inshuwa-client/models';
import { environment } from '../environments/environment';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { SharedAuthService } from '../services/auth.service';
import { IMPERSONATION_READ_ONLY_ERROR_CODE } from './impersonation-read-only.interceptor';
import { GetDecodedToken } from '../utils/token-decode';
import {
  getStoredLoginResponse,
  setStoredLoginResponse,
} from '../utils/auth-storage';

@Injectable()
export class IcHttpConfigInterceptor implements HttpInterceptor {
  env = environment;
  private notificationService = inject(NzNotificationService);
  private loginService = inject(SharedAuthService);

  // Shared subject to queue requests during refresh
  private static refreshInProgress = false;
  private static refreshSubject: ReplaySubject<ILoginResponse | null> =
    new ReplaySubject(1);

  private static getStatusDescription(status: number): string {
    const descriptions: Record<number, string> = {
      0: 'Unable to reach the server. Please check your connection and try again.',
      400: 'Bad request',
      401: 'Unauthorized',
      403: 'Access denied',
      404: 'Resource not found',
      408: 'Request timeout',
      409: 'Conflict',
      422: 'Unprocessable request',
      429: 'Too many requests',
      500: 'Internal server error',
      502: 'Bad gateway',
      503: 'Service unavailable',
      504: 'Gateway timeout',
    };
    return descriptions[status] ?? 'An unexpected error occurred';
  }

  private getToken(): ILoginResponse | null {
    return getStoredLoginResponse();
  }

  /**
   * Clone request with additional headers
   */
  private cloneRequestWithHeaders(
    request: HttpRequest<unknown>,
    token?: string,
    extraHeaders?: { [key: string]: string },
  ): HttpRequest<unknown> {
    let headers = request.headers
      .set('X-User-Timezone', Intl.DateTimeFormat().resolvedOptions().timeZone)
      .set('X-Request-Origin', window.location.origin)
      // Lands in Serilog as the "Environment" field (ServiceDefaults/LoggingExtensions.cs). Prefers
      // the resolved portalName over the build's environment.name, which becomes the same constant
      // for every tenant once one image serves them all - the fallback only covers the window
      // before branding resolves.
      .set('X-Environment', this.env.portalName || this.env.name);
    if (token) {
      headers = headers.set('Authorization', 'Bearer ' + token);
    }

    const ssoToken = localStorage.getItem('x-sso-token');

    if (ssoToken) {
      headers = headers.set(
        'X-Sso-Token',
        localStorage.getItem('x-sso-token')!,
      );
    }
    if (extraHeaders) {
      Object.entries(extraHeaders).forEach(([key, value]) => {
        headers = headers.set(key, value);
      });
    }
    return request.clone({ headers });
  }

  /**
   * Handle token refresh logic
   */
  private handleRefresh(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    token: ILoginResponse,
  ): Observable<HttpEvent<unknown>> {
    const id = GetDecodedToken()!.id;
    if (!IcHttpConfigInterceptor.refreshInProgress) {
      IcHttpConfigInterceptor.refreshInProgress = true;
      this.loginService
        .refreshToken(id, token.refreshToken)
        .pipe(
          finalize(() => {
            IcHttpConfigInterceptor.refreshInProgress = false;
          }),
        )
        .subscribe({
          next: (newToken: ILoginResponse) => {
            setStoredLoginResponse(newToken);
            IcHttpConfigInterceptor.refreshSubject.next(newToken);
          },
          error: (refreshErr) => {
            IcHttpConfigInterceptor.refreshSubject.next(null);
            this.logout(refreshErr);
          },
        });
    }
    // Wait for the refresh to complete, then retry or logout
    return IcHttpConfigInterceptor.refreshSubject.pipe(
      filter((t) => t !== undefined),
      take(1),
      switchMap((newToken) => {
        if (newToken) {
          const retryReq = this.cloneRequestWithHeaders(
            request,
            newToken.accessToken,
            { 'X-Disallow-Cache': '1' },
          );
          return next.handle(retryReq);
        } else {
          return this.logout();
        }
      }),
    );
  }

  /**
   * Logout and notify user
   */
  private logout(refreshErr: any = null) {
    this.notificationService.error(
      'Session expired',
      'Your session has expired, please login again',
      { nzDuration: 60000 },
    );
    // Only this tab's session expired; other tabs may be signed in as other accounts.
    setTimeout(() => {
      this.loginService.logOut(false);
    }, 2000);
    return throwError(() => refreshErr);
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Pre-signed S3/Minio URLs carry their own auth in the query string; adding a Bearer
    // token makes the storage server reject them ("multiple authentication types").
    if (isPresignedStorageUrl(request.url)) {
      return next.handle(request);
    }

    const token = this.getToken();
    let outgoingRequest = this.cloneRequestWithHeaders(
      request,
      token?.accessToken,
    );

    if (this.env.debug) {
      console.log('================http request====================');
      console.log(outgoingRequest);
      console.log('=========================================');
    }

    return next.handle(outgoingRequest).pipe(
      map((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse && this.env.debug) {
          console.log('================http====================');
          console.log(event);
          console.log('=========================================');
        }
        return event;
      }),
      catchError((err: HttpErrorResponse) => {
        if (this.env.debug) {
          console.log('================http request error====================');
          console.log(err);
          console.log('=====================================================');
        }
        // Handle 401 with refresh token
        if (err.status === 401 && token?.refreshToken) {
          return this.handleRefresh(request, next, token);
        } else if (err.status === 401) {
          // Prevent caching of failed requests
          return this.logout();
        }
        if (err.error?.code === IMPERSONATION_READ_ONLY_ERROR_CODE) {
          return throwError(() => err);
        }
        // Handle other errors
        const serverError: IErrorModel = err.error;
        if (request.context.get(SKIP_ERROR_TOAST)) {
          throw serverError;
        }
        if (serverError?.errors) {
          this.notificationService.error(
            serverError.message,
            serverError.errors.join(', '),
            { nzDuration: 300000 },
          );
        } else {
          // status 0 = network-level failure (server unreachable, CORS, DNS) - err.message is
          // always the same generic "Http failure response for <url>: 0 Unknown Error"
          // boilerplate here, so raw mode gives no extra signal even in dev.
          const errorMessage = this.env.debug && err.status !== 0
            ? err.message
            : IcHttpConfigInterceptor.getStatusDescription(err.status);
          this.notificationService.error('Error', errorMessage, {
            nzDuration: 60000,
          });
        }
        throw serverError;
      }),
    );
  }
}

export function isPresignedStorageUrl(url: string): boolean {
  return /[?&]X-Amz-Signature=/i.test(url);
}
